/* eslint-disable no-use-before-define */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiDeposit, ApiMutualFundAssetRead, ApiSegregatedPortfolioAssetRead } from 'api/rtk-api'
import _ from 'lodash'
import { all, call, debounce, put, select, spawn, takeLatest } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import api from '@/api/_legacy'
import {
  ApiAccountBuilderState,
  ApiAccountState,
  ApiClient,
  ApiClientStrategyFeeCriteria,
  ApiCPPIStrategy,
  ApiLoan,
  ApiModelPortfolio,
  ApiObservationFrequency,
  ApiPassThroughStrategy,
  ApiProjectionResponse,
  ApiSelfSelectPortfolio,
  ApiStrategyTemplate,
  ApiTIPPStrategy,
  ApiTradeBacktestResponse,
  ApiTradePerformanceCalculation,
  ApiVolControlStrategy
} from '@/api/_legacy/generated-api'
import { createLogoutExtraReducers } from '@/utilities/redux'
import { createFetchSaga } from '@/utilities/redux/create-fetch-saga'

import { Fee, SavedState, StrategyType, TradeActivationStep } from '../../../types'
import { convertNumberToPercentage, convertToPercentage, formatFees } from '../../../util'
import { Errors } from '../../../util/validator'
import {
  getDisallowedStrategiesFromCurrentState,
  getDisallowedStrategiesFromPendingState,
  mapProtectionPortfolioWeights,
  mapStrategy
} from '../../00-builder/state/mappers'
import { FundingSource } from '../../02-terms/state/terms-slice'
import { createClearingRateFee, createFundingRateFee } from '../fees'
import { Delta1State } from './delta1'
import { LeverageConstantProportionState } from './leverage-constant-proportion'
import { leverageVolatilityControl, LeverageVolControlState } from './leverage-vol-control'
import { ProtectionContinuousState } from './protection-continuous'
import { ProtectionTermState } from './protection-term'
import { RiskManagedState } from './risk-managed'
import { getFeesByCriteria, getFeesByTemplateName, getTemplatesByName } from './state-helpers'
import { validate } from './validation-rules'

const TENANT_ID = 'absa'

export const StrategyId = Object.freeze({
  DELTA_1: 'Delta1',
  RISK_MANAGED: 'RiskManaged',
  LEVERAGE_CONSTANT_PROPORTION: 'ConstantProportionLeverage',
  LEVERAGE_VOL_CONTROL: 'VolatilityControlLeverage',
  PROTECTION_CONTINUOUS: 'ContinuousProtection',
  PROTECTION_TERM: 'TermProtection'
})

export type LeverageType = 'constant-proportion' | 'vol-control'
export type ProtectionType = 'continuous' | 'term'

export type LeverageState = {
  constantProportion: LeverageConstantProportionState
  type?: LeverageType
  volControl: LeverageVolControlState
}

export type ProtectionState = {
  continuous: ProtectionContinuousState
  isApplyVolControl: boolean
  isBankGuarantee: boolean
  isHighWaterMark: boolean
  term: ProtectionTermState
  type?: ProtectionType
}

export interface StrategyState {
  delta1?: Delta1State
  id: string
  leverage?: LeverageState
  protection?: ProtectionState
  riskManaged?: RiskManagedState
  selectedStrategyName?: string
  type?: StrategyType
}

export type StrategyStepState = {
  accountId: string
  assetBeingInspected?: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  builderErrors: Errors
  canProceed: boolean
  cashInstruments?: ApiDeposit[] | ApiLoan[]
  client?: ApiClient
  disallowedStrategies: string[]
  errors: Errors
  isChartsInvalid: boolean
  isErrorGeneratingPerformanceChart: boolean
  isErrorGeneratingProjectionChart: boolean
  isGeneratingPerformanceChart: boolean
  isGeneratingProjectionChart: boolean
  isInited: boolean
  isReferenceDataFetchError: boolean
  noteMaturityDate: string
  // key = strategyName
  performanceMetrics?: Record<string, number>
  projection?: Record<string, number>[]
  protectionAssets?: (ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]
  readonlyCurrentState?: StrategyState
  readonlyPendingState?: StrategyState
  referenceDataFetchError?: any
  selectedStrategy: StrategyState
  // key = strategyName
  strategyFeeCriteria: Record<string, ApiClientStrategyFeeCriteria[]>
  strategyFees: Record<string, Fee[]>
  strategyTemplates?: ApiStrategyTemplate[]
}

interface CurrentOrPendingState {
  currentAccountState: ApiAccountState
  pendingAccountState: ApiAccountBuilderState
  shouldUsePendingState: boolean
}

const initialStrategy: StrategyState = {
  id: '',
  riskManaged: {
    isAllowed: true,
    fees: [],
    targetVolatility: {
      min: 0,
      max: 100,
      value: 50
    }
  },
  leverage: {
    constantProportion: {
      fees: [],
      isAllowed: true,
      leverageRatio: {
        min: 0,
        max: 100,
        value: 50
      }
    },
    volControl: {
      fees: [],
      isAllowed: true,
      leverageRatio: {
        min: 0,
        max: 100,
        value: 50
      },
      targetVolatility: {
        min: 0,
        max: 100,
        value: 50
      }
    }
  },
  delta1: {
    isAllowed: true,
    fees: []
  },
  protection: {
    isBankGuarantee: true,
    isApplyVolControl: false,
    isHighWaterMark: true,
    continuous: {
      isAllowed: true,
      fees: [],
      targetVolatility: {
        min: 0,
        max: 0,
        value: 0
      },
      protectionLevel: {
        min: 0,
        max: 0,
        value: 0
      },
      leverageRatio: {
        min: 0,
        max: 0,
        value: 0
      },
      defaultHighWaterMarkFrequency: 'Daily',
      highWaterMarkFrequency: 'Daily'
    },
    term: {
      isAllowed: true,
      fees: [],
      targetVolatility: {
        min: 0,
        max: 0,
        value: 0
      },
      protectionLevel: {
        min: 0,
        max: 0,
        value: 0
      },
      maturityDate: {
        min: new Date().toDateString(),
        max: new Date().toDateString(),
        value: new Date().toDateString()
      },
      leverageRatio: {
        min: 0,
        max: 0,
        value: 0
      },
      defaultHighWaterMarkFrequency: 'Daily',
      highWaterMarkFrequency: 'Daily'
    }
  }
}

export const initialState: StrategyStepState = {
  isInited: false,
  accountId: '',
  noteMaturityDate: '',
  disallowedStrategies: [],
  canProceed: false,
  errors: {},
  builderErrors: {},
  isReferenceDataFetchError: false,
  isGeneratingPerformanceChart: false,
  isGeneratingProjectionChart: false,
  isErrorGeneratingPerformanceChart: false,
  isErrorGeneratingProjectionChart: false,
  isChartsInvalid: true,
  strategyFees: {},
  strategyFeeCriteria: {},
  selectedStrategy: {
    ...initialStrategy
  },
  readonlyPendingState: {
    ...initialStrategy
  },
  readonlyCurrentState: {
    ...initialStrategy
  }
}

const clearError = (fieldName: string, state: StrategyStepState, test: any) => {
  if (test) {
    state.errors[fieldName] = undefined
  }
}

function findProtectionAsset(state: StrategyStepState, currentOrPending: CurrentOrPendingState) {
  const savedState = currentOrPending.shouldUsePendingState
    ? currentOrPending.pendingAccountState
    : currentOrPending.currentAccountState
  return state.protectionAssets.find(x => x.assetId === savedState.protectionWeights?.[0]?.assetId)
}

function createReadonlyStateFromBase(
  baseStrategyState: StrategyState,
  templateStrategyName: string,
  strategyId: string,
  apiStrategy: ApiPassThroughStrategy,
  apiAsset: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
): StrategyState {
  const updatedState = { ...baseStrategyState }
  updatedState.id = strategyId
  updatedState.selectedStrategyName = templateStrategyName

  switch (strategyId) {
    case StrategyId.DELTA_1: {
      updatedState.type = 'delta1'
      break
    }

    case StrategyId.RISK_MANAGED: {
      updatedState.type = 'riskmanaged'
      const strategy = apiStrategy as ApiVolControlStrategy
      updatedState.riskManaged.targetVolatility.value = convertNumberToPercentage(
        strategy.targetVolatility
      )
      updatedState.riskManaged.protectionAsset = apiAsset
      break
    }

    case StrategyId.LEVERAGE_CONSTANT_PROPORTION: {
      updatedState.type = 'leverage'
      updatedState.leverage.type = 'constant-proportion'
      updatedState.leverage.constantProportion.leverageRatio.value = apiStrategy.leverageRatio
      break
    }

    case StrategyId.LEVERAGE_VOL_CONTROL: {
      updatedState.type = 'leverage'
      updatedState.leverage.type = 'vol-control'
      updatedState.leverage.volControl.leverageRatio.value = apiStrategy.leverageRatio
      const strategy = apiStrategy as ApiVolControlStrategy
      updatedState.leverage.volControl.targetVolatility.value = convertNumberToPercentage(
        strategy.targetVolatility
      )
      updatedState.leverage.volControl.protectionAsset = apiAsset
      break
    }

    case StrategyId.PROTECTION_CONTINUOUS: {
      updatedState.type = 'protection'
      updatedState.protection.type = 'continuous'
      const strategy = apiStrategy as ApiTIPPStrategy
      const { continuous } = updatedState.protection
      continuous.highWaterMarkFrequency = strategy.highWatermarkObservationFrequency
      continuous.leverageRatio.value = strategy.leverageRatio
      continuous.protectionAsset = apiAsset
      continuous.protectionLevel.value = convertNumberToPercentage(strategy.protectionPercentage)
      continuous.targetVolatility.value = convertNumberToPercentage(strategy.targetVolatility)
      updatedState.protection.isBankGuarantee = strategy.hasBankGuarantee
      updatedState.protection.isHighWaterMark =
        strategy.highWatermarkObservationFrequency !== 'None'
      updatedState.protection.isApplyVolControl = strategy.targetVolatility !== undefined
      // TODO: ignoring setting of fees for readonly state
      break
    }

    case StrategyId.PROTECTION_TERM: {
      updatedState.type = 'protection'
      updatedState.protection.type = 'term'
      const strategy = apiStrategy as ApiCPPIStrategy
      const { term } = updatedState.protection
      term.highWaterMarkFrequency = strategy.highWatermarkObservationFrequency
      term.leverageRatio.value = strategy.leverageRatio
      term.protectionAsset = apiAsset
      term.protectionLevel.value = convertNumberToPercentage(strategy.protectionPercentage)
      term.targetVolatility.value = convertNumberToPercentage(strategy.targetVolatility)
      updatedState.protection.isBankGuarantee = strategy.hasBankGuarantee
      updatedState.protection.isHighWaterMark =
        strategy.highWatermarkObservationFrequency !== 'None'
      updatedState.protection.isApplyVolControl = strategy.targetVolatility !== undefined
      term.maturityDate.value = strategy.maturityDate
      // TODO: ignoring setting of fees for readonly state
      break
    }
  }
  return updatedState
}

export const strategySlice = createSlice({
  name: 'strategyStep',
  initialState,
  reducers: {
    resetStrategy: state => {
      state.selectedStrategy = initialState.selectedStrategy
      state.readonlyCurrentState = initialState.readonlyCurrentState
      state.readonlyPendingState = initialState.readonlyPendingState
      state.performanceMetrics = undefined
      state.projection = undefined
      state.isGeneratingPerformanceChart = false
      state.isGeneratingProjectionChart = false
      state.isErrorGeneratingPerformanceChart = false
      state.isErrorGeneratingProjectionChart = false
      state.isChartsInvalid = true
      state.canProceed = false
      state.errors = {}
      state.builderErrors = {}
      state.assetBeingInspected = undefined
    },
    // the client and accountId are passed in when the <StrategyStep> component is created
    initNew: (
      state,
      action: PayloadAction<{
        accountId: string
        client: ApiClient
      }>
    ) => {
      state.client = action.payload.client
      state.accountId = action.payload.accountId
    },
    initExisting: (state, action: PayloadAction<{ accountId: string; client: ApiClient }>) => {
      state.client = action.payload.client
      state.accountId = action.payload.accountId
    },
    inspectAsset: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      const asset = action.payload
      state.assetBeingInspected = asset as any
    },
    stopInspectingAsset: state => {
      state.assetBeingInspected = undefined
    },
    setReadonlyCurrentOrPending: (
      state,
      action: PayloadAction<ApiAccountState | ApiAccountBuilderState>
    ) => {
      if (action.payload && action.payload.strategy) {
        const currentAccountState = action.payload as ApiAccountState
        const pendingAccountState = action.payload as ApiAccountBuilderState
        const isPending = (action.payload as ApiAccountBuilderState).accountName === undefined

        const selectedStrategyTemplateId = isPending
          ? pendingAccountState.strategyTemplateId
          : currentAccountState.strategyTemplateId
        const strategyTemplate = state.strategyTemplates.find(
          x => x.strategyTemplateId === selectedStrategyTemplateId
        )
        const strategyId = action.payload.strategyTemplateId
        const apiAsset = state.protectionAssets.find(
          x => x.assetId === action.payload.protectionWeights?.[0]?.assetId
        )

        const stateToUpdate = isPending
          ? createReadonlyStateFromBase(
              state.readonlyPendingState,
              strategyTemplate.strategyName,
              strategyId,
              action.payload.strategy,
              apiAsset
            )
          : createReadonlyStateFromBase(
              state.readonlyCurrentState,
              strategyTemplate.strategyName,
              strategyId,
              action.payload.strategy,
              apiAsset
            )

        if (isPending) {
          state.readonlyPendingState = stateToUpdate
        } else {
          state.readonlyCurrentState = stateToUpdate
        }
      }
    },
    setStrategiesExisting: (
      state,
      action: PayloadAction<{ disallowedStrategies: string[]; isTransfer: boolean }>
    ) => {
      const { isTransfer } = action.payload
      state.disallowedStrategies = action.payload.disallowedStrategies
      if (state.strategyTemplates) {
        const strategyTemplates = getTemplatesByName(state.strategyTemplates)
        state.selectedStrategy.delta1.isAllowed = !(
          strategyTemplates.delta1 === undefined ||
          state.disallowedStrategies.includes(strategyTemplates.delta1.strategyTemplateId)
        )
        state.selectedStrategy.riskManaged.isAllowed = !(
          strategyTemplates.riskManaged === undefined ||
          state.disallowedStrategies.includes(strategyTemplates.riskManaged.strategyTemplateId) ||
          isTransfer
        )
        state.selectedStrategy.leverage.constantProportion.isAllowed = !(
          strategyTemplates.leverageConstantProportion === undefined ||
          state.disallowedStrategies.includes(
            strategyTemplates.leverageConstantProportion.strategyTemplateId
          ) ||
          isTransfer
        )
        state.selectedStrategy.leverage.volControl.isAllowed = !(
          strategyTemplates.leverageVolatilityControl === undefined ||
          state.disallowedStrategies.includes(
            strategyTemplates.leverageVolatilityControl.strategyTemplateId
          ) ||
          isTransfer
        )
        state.selectedStrategy.protection.term.isAllowed = !(
          strategyTemplates.protectionCppi === undefined ||
          state.disallowedStrategies.includes(
            strategyTemplates.protectionCppi.strategyTemplateId
          ) ||
          isTransfer
        )
        state.selectedStrategy.protection.continuous.isAllowed = !(
          strategyTemplates.protectionTipp === undefined ||
          state.disallowedStrategies.includes(
            strategyTemplates.protectionTipp.strategyTemplateId
          ) ||
          isTransfer
        )
      }
    },
    setType: (state, action: PayloadAction<StrategyType>) => {
      state.selectedStrategy.type = action.payload
    },
    setId: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.id = action.payload
    },
    setChartRefresh: () => {
      // dummy comment to keep linter happy, handled by builder saga
    },
    validateStrategy: state => {
      const validationResult = validate(state)
      state.canProceed = validationResult.isValid
      state.errors = validationResult.errors
    },
    setRiskManagedTargetVolatility: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.riskManaged.targetVolatility.value = action.payload
    },
    setRiskManagedProtectionAsset: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.selectedStrategy.riskManaged.protectionAsset = action.payload as any
      clearError(
        'riskManaged_protectionAsset',
        state,
        state.selectedStrategy.riskManaged.protectionAsset
      )
    },
    setLeverageType: (state, action: PayloadAction<LeverageType>) => {
      state.selectedStrategy.leverage.type = action.payload
      if (action.payload === 'constant-proportion') {
        state.selectedStrategy.id = StrategyId.LEVERAGE_CONSTANT_PROPORTION
      }
      if (action.payload === 'vol-control') {
        state.selectedStrategy.id = StrategyId.LEVERAGE_VOL_CONTROL
      }
    },
    setLeverageConstantProportionLeverageRatio: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.leverage.constantProportion.leverageRatio.value = Number(
        action.payload
      )
    },
    setLeverageVolControlLeverageRatio: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.leverage.volControl.leverageRatio.value = Number(action.payload)
    },
    setLeverageTargetVolatility: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.leverage.volControl.targetVolatility.value = Number(action.payload)
    },
    setLeverageVolControlProtectionAsset: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.selectedStrategy.leverage.volControl.protectionAsset = action.payload as any
    },
    setProtectionType: (state, action: PayloadAction<ProtectionType>) => {
      state.selectedStrategy.protection.type = action.payload
      if (action.payload === 'continuous') {
        state.selectedStrategy.id = StrategyId.PROTECTION_CONTINUOUS
      }
      if (action.payload === 'term') {
        state.selectedStrategy.id = StrategyId.PROTECTION_TERM
      }
    },
    rehydrateRiskManaged: (state, action: PayloadAction<CurrentOrPendingState>) => {
      const currentOrPendingStrategy = action.payload.shouldUsePendingState
        ? action.payload.pendingAccountState.strategy
        : action.payload.currentAccountState.strategy
      const strategy = currentOrPendingStrategy as ApiVolControlStrategy
      state.selectedStrategy.riskManaged.targetVolatility.value = convertNumberToPercentage(
        strategy.targetVolatility
      )
      state.selectedStrategy.riskManaged.protectionAsset = findProtectionAsset(
        state,
        action.payload
      )
    },
    rehydrateLeverageConstantProportion: (state, action: PayloadAction<CurrentOrPendingState>) => {
      const currentOrPendingStrategy = action.payload.shouldUsePendingState
        ? action.payload.pendingAccountState.strategy
        : action.payload.currentAccountState.strategy
      state.selectedStrategy.leverage.constantProportion.leverageRatio.value =
        currentOrPendingStrategy.leverageRatio
    },
    rehydrateLeverageVolControl: (state, action: PayloadAction<CurrentOrPendingState>) => {
      const currentOrPendingStrategy = action.payload.shouldUsePendingState
        ? action.payload.pendingAccountState.strategy
        : action.payload.currentAccountState.strategy
      const strategy = currentOrPendingStrategy as ApiVolControlStrategy
      state.selectedStrategy.leverage.volControl.leverageRatio.value = strategy.leverageRatio
      state.selectedStrategy.leverage.volControl.targetVolatility.value = convertNumberToPercentage(
        strategy.targetVolatility
      )
      state.selectedStrategy.leverage.volControl.protectionAsset = findProtectionAsset(
        state,
        action.payload
      ) as any
    },
    rehydrateProtectionContinuous: (state, action: PayloadAction<CurrentOrPendingState>) => {
      const currentOrPendingStrategy = action.payload.shouldUsePendingState
        ? action.payload.pendingAccountState.strategy
        : action.payload.currentAccountState.strategy
      const strategy = currentOrPendingStrategy as ApiTIPPStrategy
      const { continuous } = state.selectedStrategy.protection
      continuous.highWaterMarkFrequency = strategy.highWatermarkObservationFrequency
      continuous.leverageRatio.value = strategy.leverageRatio
      continuous.protectionAsset = findProtectionAsset(state, action.payload)
      continuous.protectionLevel.value = convertNumberToPercentage(strategy.protectionPercentage)
      continuous.targetVolatility.value = convertNumberToPercentage(strategy.targetVolatility)
      state.selectedStrategy.protection.isBankGuarantee = strategy.hasBankGuarantee
      state.selectedStrategy.protection.isHighWaterMark =
        strategy.highWatermarkObservationFrequency !== 'None'
      state.selectedStrategy.protection.isApplyVolControl = strategy.targetVolatility !== undefined
      continuous.fees = getFeesByCriteria(
        state.strategyFeeCriteria[StrategyId.PROTECTION_CONTINUOUS],
        state.selectedStrategy.protection.isBankGuarantee,
        continuous.leverageRatio.value,
        state.selectedStrategy.protection.isApplyVolControl
      )
      state.selectedStrategy.protection.continuous.fees.push(
        createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
      )
      state.selectedStrategy.protection.continuous.fees.push(
        createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
      )
      state.strategyFees[StrategyId.PROTECTION_CONTINUOUS] =
        state.selectedStrategy.protection.continuous.fees
    },
    rehydrateProtectionTerm: (state, action: PayloadAction<CurrentOrPendingState>) => {
      const currentOrPendingStrategy = action.payload.shouldUsePendingState
        ? action.payload.pendingAccountState.strategy
        : action.payload.currentAccountState.strategy
      const strategy = currentOrPendingStrategy as ApiCPPIStrategy
      const { term } = state.selectedStrategy.protection
      term.highWaterMarkFrequency = strategy.highWatermarkObservationFrequency
      term.leverageRatio.value = strategy.leverageRatio
      term.protectionAsset = findProtectionAsset(state, action.payload)
      term.protectionLevel.value = convertNumberToPercentage(strategy.protectionPercentage)
      term.targetVolatility.value = convertNumberToPercentage(strategy.targetVolatility)
      state.selectedStrategy.protection.isBankGuarantee = strategy.hasBankGuarantee
      state.selectedStrategy.protection.isHighWaterMark =
        strategy.highWatermarkObservationFrequency !== 'None'
      state.selectedStrategy.protection.isApplyVolControl = strategy.targetVolatility !== undefined
      term.maturityDate.value = strategy.maturityDate
      term.fees = getFeesByCriteria(
        state.strategyFeeCriteria[StrategyId.PROTECTION_TERM],
        state.selectedStrategy.protection.isBankGuarantee,
        term.leverageRatio.value,
        state.selectedStrategy.protection.isApplyVolControl
      )
      state.selectedStrategy.protection.term.fees.push(
        createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
      )
      state.selectedStrategy.protection.term.fees.push(
        createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
      )
      state.strategyFees[StrategyId.PROTECTION_TERM] = state.selectedStrategy.protection.term.fees
    },
    setProtectionIsBankGuarantee: (state, action: PayloadAction<boolean>) => {
      state.selectedStrategy.protection.isBankGuarantee = action.payload
      switch (state.selectedStrategy.protection.type) {
        case 'continuous':
          state.selectedStrategy.protection.continuous.fees = getFeesByCriteria(
            state.strategyFeeCriteria[StrategyId.PROTECTION_CONTINUOUS],
            state.selectedStrategy.protection.isBankGuarantee,
            state.selectedStrategy.protection.continuous.leverageRatio.value,
            state.selectedStrategy.protection.isApplyVolControl
          )
          state.selectedStrategy.protection.continuous.fees.push(
            createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.selectedStrategy.protection.continuous.fees.push(
            createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.strategyFees[StrategyId.PROTECTION_CONTINUOUS] =
            state.selectedStrategy.protection.continuous.fees
          break
        case 'term':
          state.selectedStrategy.protection.term.fees = getFeesByCriteria(
            state.strategyFeeCriteria[StrategyId.PROTECTION_TERM],
            state.selectedStrategy.protection.isBankGuarantee,
            state.selectedStrategy.protection.term.leverageRatio.value,
            state.selectedStrategy.protection.isApplyVolControl
          )
          state.selectedStrategy.protection.term.fees.push(
            createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.selectedStrategy.protection.term.fees.push(
            createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.strategyFees[StrategyId.PROTECTION_TERM] =
            state.selectedStrategy.protection.term.fees
      }
    },
    setProtectionIsApplyVolatilityControl: (state, action: PayloadAction<boolean>) => {
      state.selectedStrategy.protection.isApplyVolControl = action.payload
      switch (state.selectedStrategy.protection.type) {
        case 'continuous':
          state.selectedStrategy.protection.continuous.fees = getFeesByCriteria(
            state.strategyFeeCriteria[StrategyId.PROTECTION_CONTINUOUS],
            state.selectedStrategy.protection.isBankGuarantee,
            state.selectedStrategy.protection.continuous.leverageRatio.value,
            state.selectedStrategy.protection.isApplyVolControl
          )
          state.selectedStrategy.protection.continuous.fees.push(
            createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.selectedStrategy.protection.continuous.fees.push(
            createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.strategyFees[StrategyId.PROTECTION_CONTINUOUS] =
            state.selectedStrategy.protection.continuous.fees
          break
        case 'term':
          state.selectedStrategy.protection.term.fees = getFeesByCriteria(
            state.strategyFeeCriteria[StrategyId.PROTECTION_TERM],
            state.selectedStrategy.protection.isBankGuarantee,
            state.selectedStrategy.protection.term.leverageRatio.value,
            state.selectedStrategy.protection.isApplyVolControl
          )
          state.selectedStrategy.protection.term.fees.push(
            createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.selectedStrategy.protection.term.fees.push(
            createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
          )
          state.strategyFees[StrategyId.PROTECTION_TERM] =
            state.selectedStrategy.protection.term.fees
      }
    },
    setProtectionTargetVolatility: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.protection[
        state.selectedStrategy.protection.type
      ].targetVolatility.value = Number(action.payload)
    },
    setProtectionLevel: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.protection[
        state.selectedStrategy.protection.type
      ].protectionLevel.value = Number(action.payload)
    },
    setProtectionHighWaterMark: (state, action: PayloadAction<boolean>) => {
      state.selectedStrategy.protection.isHighWaterMark = action.payload

      if (action.payload === true) {
        const defaultValue =
          state.selectedStrategy.protection[state.selectedStrategy.protection.type]
            .defaultHighWaterMarkFrequency
        state.selectedStrategy.protection[
          state.selectedStrategy.protection.type
        ].highWaterMarkFrequency = defaultValue
      } else {
        state.selectedStrategy.protection[
          state.selectedStrategy.protection.type
        ].highWaterMarkFrequency = 'None'
      }
    },
    setProtectionHighWaterMarkFrequency: (state, action: PayloadAction<string>) => {
      const value = action.payload as ApiObservationFrequency
      state.selectedStrategy.protection[
        state.selectedStrategy.protection.type
      ].highWaterMarkFrequency = value
    },
    setProtectionMaturityDate: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.protection.term.maturityDate.value = action.payload
    },
    setProtectionLeverageRatio: (state, action: PayloadAction<string>) => {
      state.selectedStrategy.protection[
        state.selectedStrategy.protection.type
      ].leverageRatio.value = Number(action.payload)
    },
    setProtectionContinuousProtectionAsset: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.selectedStrategy.protection.continuous.protectionAsset = action.payload as any
    },
    setProtectionTermProtectionAsset: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.selectedStrategy.protection.term.protectionAsset = action.payload as any
    },
    fetchAssetsSuccess: (
      state,
      action: PayloadAction<(ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]>
    ) => {
      state.protectionAssets = action.payload.filter(
        x => x.protectionClassification === 'Protection'
      ) as any
    },
    setIsInited: state => {
      state.isInited = true
    },
    fetchReferenceDataSuccess: (
      state,
      action: PayloadAction<{
        cashInstruments: ApiDeposit[] | ApiLoan[]
        isTransfer: boolean
        noteMatDate: string
        strategyTemplates: ApiStrategyTemplate[]
      }>
    ) => {
      state.strategyTemplates = action.payload.strategyTemplates
      state.cashInstruments = action.payload.cashInstruments
      state.noteMaturityDate = action.payload.noteMatDate
      const { isTransfer } = action.payload

      const templates = getTemplatesByName(state.strategyTemplates)
      const fees = getFeesByTemplateName(state.client, templates)
      const { disallowedStrategies } = state

      state.selectedStrategy.delta1.fees = []
      state.selectedStrategy.riskManaged.fees = []
      state.selectedStrategy.leverage.constantProportion.fees = []
      state.selectedStrategy.leverage.volControl.fees = []
      state.selectedStrategy.protection.continuous.fees = []
      state.selectedStrategy.protection.term.fees = []

      state.selectedStrategy.delta1.isAllowed = false
      state.selectedStrategy.riskManaged.isAllowed = false
      state.selectedStrategy.leverage.constantProportion.isAllowed = false
      state.selectedStrategy.leverage.volControl.isAllowed = false
      state.selectedStrategy.protection.continuous.isAllowed = false
      state.selectedStrategy.protection.term.isAllowed = false

      if (fees.delta1) {
        state.strategyFeeCriteria[StrategyId.DELTA_1] = fees.delta1
        if (fees.delta1.length !== 0) {
          state.selectedStrategy.delta1.fees = formatFees(
            state.strategyFeeCriteria[StrategyId.DELTA_1][0].fees
          )
        }
      }

      if (fees.riskManaged) {
        state.strategyFeeCriteria[StrategyId.RISK_MANAGED] = fees.riskManaged
        if (fees.riskManaged.length !== 0) {
          state.selectedStrategy.riskManaged.fees = formatFees(
            state.strategyFeeCriteria[StrategyId.RISK_MANAGED][0].fees
          )
        }
      }

      if (fees.leverageConstantProportion) {
        state.strategyFeeCriteria[StrategyId.LEVERAGE_CONSTANT_PROPORTION] =
          fees.leverageConstantProportion
        if (fees.leverageConstantProportion.length !== 0) {
          state.selectedStrategy.leverage.constantProportion.fees = formatFees(
            state.strategyFeeCriteria[StrategyId.LEVERAGE_CONSTANT_PROPORTION][0].fees
          )
        }
      }

      if (fees.leverageVolatilityControl) {
        state.strategyFeeCriteria[StrategyId.LEVERAGE_VOL_CONTROL] = fees.leverageVolatilityControl
        if (fees.leverageVolatilityControl.length !== 0) {
          state.selectedStrategy.leverage.volControl.fees = formatFees(
            state.strategyFeeCriteria[StrategyId.LEVERAGE_VOL_CONTROL][0].fees
          )
        }
      }

      if (fees.protectionTipp) {
        state.strategyFeeCriteria[StrategyId.PROTECTION_CONTINUOUS] = fees.protectionTipp
        const initialTippFeeCriteria = fees.protectionTipp.find(
          x =>
            x.hasBankGuarantee === state.selectedStrategy.protection.isBankGuarantee &&
            !x.isLeveraged
        )
        if (initialTippFeeCriteria !== undefined) {
          state.selectedStrategy.protection.continuous.fees = formatFees(
            state.selectedStrategy.protection.isApplyVolControl
              ? initialTippFeeCriteria.fees
              : initialTippFeeCriteria.fees.filter(
                  fee => fee.feeType !== 'RiskControlledPremiumFee'
                )
          )
        }
      }

      if (fees.protectionCppi) {
        state.strategyFeeCriteria[StrategyId.PROTECTION_TERM] = fees.protectionCppi
        const initialCppiFeeCriteria = fees.protectionCppi.find(
          x =>
            x.hasBankGuarantee === state.selectedStrategy.protection.isBankGuarantee &&
            !x.isLeveraged
        )
        if (initialCppiFeeCriteria !== undefined) {
          state.selectedStrategy.protection.term.fees = formatFees(
            state.selectedStrategy.protection.isApplyVolControl
              ? initialCppiFeeCriteria.fees
              : initialCppiFeeCriteria.fees.filter(
                  fee => fee.feeType !== 'RiskControlledPremiumFee'
                )
          )
        }
      }

      if (templates.delta1 && !disallowedStrategies.includes(templates.delta1.strategyTemplateId)) {
        state.selectedStrategy.delta1.isAllowed = true
        state.selectedStrategy.delta1.fees.push(
          createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        state.strategyFees[StrategyId.DELTA_1] = state.selectedStrategy.delta1.fees
      }

      if (
        templates.riskManaged &&
        !disallowedStrategies.includes(templates.riskManaged.strategyTemplateId) &&
        !isTransfer
      ) {
        const { riskManaged } = state.selectedStrategy
        riskManaged.isAllowed = true
        riskManaged.fees.push(
          createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )
        state.strategyFees[StrategyId.RISK_MANAGED] = state.selectedStrategy.riskManaged.fees

        riskManaged.targetVolatility.min = templates.riskManaged.minTargetVolatility
        riskManaged.targetVolatility.max = templates.riskManaged.maxTargetVolatility
        riskManaged.targetVolatility.value = templates.riskManaged.defaultTargetVolatility

        riskManaged.targetVolatility = convertToPercentage(riskManaged.targetVolatility)
      }

      if (
        templates.leverageConstantProportion &&
        !disallowedStrategies.includes(templates.leverageConstantProportion.strategyTemplateId) &&
        !isTransfer
      ) {
        state.selectedStrategy.leverage.constantProportion.fees.push(
          createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        state.selectedStrategy.leverage.constantProportion.fees.push(
          createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        state.strategyFees[StrategyId.LEVERAGE_CONSTANT_PROPORTION] =
          state.selectedStrategy.leverage.constantProportion.fees

        state.selectedStrategy.leverage.constantProportion.isAllowed = true

        state.selectedStrategy.leverage.constantProportion.leverageRatio.min =
          templates.leverageConstantProportion.minLeverageRatio
        state.selectedStrategy.leverage.constantProportion.leverageRatio.max =
          templates.leverageConstantProportion.maxLeverageRatio
        state.selectedStrategy.leverage.constantProportion.leverageRatio.value =
          templates.leverageConstantProportion.defaultLeverageRatio
      }

      if (
        templates.leverageVolatilityControl &&
        !disallowedStrategies.includes(templates.leverageVolatilityControl.strategyTemplateId) &&
        !isTransfer
      ) {
        const { volControl } = state.selectedStrategy.leverage

        leverageVolatilityControl.initializeStateFromTemplate(
          templates.leverageVolatilityControl,
          volControl,
          state.client,
          state.cashInstruments as any
        )
        state.strategyFees[StrategyId.LEVERAGE_VOL_CONTROL] = volControl.fees
      }

      if (
        templates.protectionTipp &&
        !disallowedStrategies.includes(templates.protectionTipp.strategyTemplateId) &&
        !isTransfer
      ) {
        const { continuous } = state.selectedStrategy.protection
        continuous.isAllowed = true
        continuous.fees.push(
          createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        state.selectedStrategy.protection.continuous.fees.push(
          createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        continuous.protectionLevel.min = templates.protectionTipp.minProtectionLevel
        continuous.protectionLevel.max = templates.protectionTipp.maxProtectionLevel
        continuous.protectionLevel.value = templates.protectionTipp.defaultProtectionLevel

        continuous.leverageRatio.min = templates.protectionTipp.minLeverageRatio
        continuous.leverageRatio.max = templates.protectionTipp.maxLeverageRatio
        continuous.leverageRatio.value = templates.protectionTipp.defaultLeverageRatio

        continuous.targetVolatility.min = templates.protectionTipp.minTargetVolatility
        continuous.targetVolatility.max = templates.protectionTipp.maxTargetVolatility
        continuous.targetVolatility.value = templates.protectionTipp.defaultTargetVolatility

        continuous.targetVolatility = convertToPercentage(continuous.targetVolatility)
        continuous.protectionLevel = convertToPercentage(continuous.protectionLevel)

        state.strategyFees[StrategyId.PROTECTION_CONTINUOUS] =
          state.selectedStrategy.protection.continuous.fees
      }

      if (
        templates.protectionCppi &&
        !disallowedStrategies.includes(templates.protectionCppi.strategyTemplateId) &&
        !isTransfer
      ) {
        const { term } = state.selectedStrategy.protection
        term.isAllowed = true

        term.fees.push(
          createClearingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        term.fees.push(
          createFundingRateFee(state.client.defaultCashInstruments, state.cashInstruments as any)
        )

        state.strategyFees[StrategyId.PROTECTION_TERM] = state.selectedStrategy.protection.term.fees

        term.protectionLevel.min = templates.protectionCppi.minProtectionLevel
        term.protectionLevel.max = templates.protectionCppi.maxProtectionLevel
        term.protectionLevel.value = templates.protectionCppi.defaultProtectionLevel

        term.leverageRatio.min = templates.protectionCppi.minLeverageRatio
        term.leverageRatio.max = templates.protectionCppi.maxLeverageRatio
        term.leverageRatio.value = templates.protectionCppi.defaultLeverageRatio

        term.targetVolatility.min = templates.protectionCppi.minTargetVolatility
        term.targetVolatility.max = templates.protectionCppi.maxTargetVolatility
        term.targetVolatility.value = templates.protectionCppi.defaultTargetVolatility

        const minDate = new Date()
        minDate.setMonth(
          minDate.getMonth() + templates.protectionCppi.minNumberOfMonthsToTimeToMaturity
        )
        term.maturityDate.min = `${String(minDate.getFullYear())}-${String(
          minDate.getMonth() + 1
        )}-${String(minDate.getDate())}`
        const maxDate = new Date()
        term.maturityDate.value = term.maturityDate.min
        maxDate.setMonth(
          maxDate.getMonth() + templates.protectionCppi.maxNumberOfMonthsToTimeToMaturity
        )
        let lowestMaxDate: Date
        if (state.noteMaturityDate === undefined) {
          lowestMaxDate = maxDate
        } else {
          const noteMatDateObj = new Date(state.noteMaturityDate)
          lowestMaxDate = maxDate < noteMatDateObj ? maxDate : noteMatDateObj
        }
        term.maturityDate.max = `${String(lowestMaxDate.getFullYear())}-${String(
          lowestMaxDate.getMonth() + 1
        )}-${String(lowestMaxDate.getDate())}`

        term.targetVolatility = convertToPercentage(term.targetVolatility)
        term.protectionLevel = convertToPercentage(term.protectionLevel)
      }

      state.isReferenceDataFetchError = false
    },
    fetchReferenceDataFailure: (state, action) => {
      state.isReferenceDataFetchError = true
      state.referenceDataFetchError = action.payload
    },
    beginFetchBacktest: state => {
      state.isErrorGeneratingPerformanceChart = false
      state.isGeneratingPerformanceChart = true
    },
    beginFetchProjection: state => {
      state.isErrorGeneratingProjectionChart = false
      state.isGeneratingProjectionChart = true
    },
    fetchBacktestSuccess: (state, action: PayloadAction<Record<string, number>>) => {
      state.isErrorGeneratingPerformanceChart = false
      state.isGeneratingPerformanceChart = false
      state.performanceMetrics = action.payload
    },
    fetchBacktestFailure: state => {
      state.isGeneratingPerformanceChart = false
      state.isErrorGeneratingPerformanceChart = true
    },
    fetchProjectionSuccess: (state, action: PayloadAction<Record<string, number>[]>) => {
      state.isErrorGeneratingProjectionChart = false
      state.isGeneratingProjectionChart = false
      state.projection = action.payload
    },
    fetchProjectionFailure: state => {
      state.isGeneratingProjectionChart = false
      state.isErrorGeneratingProjectionChart = true
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
      state.isChartsInvalid = true
    },
    setBuilderErrors: (state, action: PayloadAction<Errors>) => {
      state.builderErrors = action.payload
    },
    setDataIncomplete: (state, action: PayloadAction<Errors>) => {
      state.isChartsInvalid = true
      state.isGeneratingPerformanceChart = false
      state.isGeneratingProjectionChart = false
      state.canProceed = false
      state.errors = action.payload
    },
    setDataComplete: state => {
      state.isChartsInvalid = false
      state.canProceed = true
      state.errors = {}
    },
    moveNext: () => {
      // dummy comment to keep linter happy
    },
    updateAndMoveNext: () => {
      // dummy comment to keep linter happy, handled by builder saga
    },
    moveBack: () => {
      // dummy comment to keep linter happy
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

const init = (params: { accountId: string; client: ApiClient }) => (dispatch, getState) => {
  if (getState().strategy.isInited) {
    dispatch(strategySlice.actions.initExisting())
  } else {
    dispatch(strategySlice.actions.initNew(params))
  }
}

export interface MergeStrategiesParams {
  busineessUnitDisallowed: string[]
  modelPortfolio: ApiModelPortfolio
  selfSelectPortfolio: ApiSelfSelectPortfolio
}

const requestMoveNext = () => (dispatch, getState: () => { strategy: StrategyStepState }) => {
  const state = getState().strategy

  const result = validate(state)

  if (result.isValid) {
    dispatch(actions.moveNext())
  } else {
    dispatch(actions.setInvalid(result.errors))
  }
}

const loadState = (payload: SavedState) => ({ type: 'strategyStep/loadState', payload })

export const actions = {
  ...strategySlice.actions,
  requestMoveNext,
  loadState,
  init
}
export type StrategyActions = typeof actions

// TODO: need to have a "gateway" / anti corruption layer / middleware that just gives me back a strategy
//       in the format I need here.
//       when it comes to saving, the gateway would convert back to the server format

function* rehydrateStrategySaga(
  strategyTemplate: ApiStrategyTemplate,
  currentOrPending: CurrentOrPendingState
) {
  yield put(actions.setId(strategyTemplate.strategyTemplateId))
  switch (strategyTemplate.strategyTemplateId) {
    case StrategyId.DELTA_1: {
      yield put(actions.setType('delta1'))
      break
    }

    case StrategyId.RISK_MANAGED: {
      yield put(actions.setType('riskmanaged'))
      yield put(actions.rehydrateRiskManaged(currentOrPending))
      break
    }

    case StrategyId.LEVERAGE_CONSTANT_PROPORTION: {
      yield put(actions.setType('leverage'))
      yield put(actions.setLeverageType('constant-proportion'))
      yield put(actions.rehydrateLeverageConstantProportion(currentOrPending))
      break
    }

    case StrategyId.LEVERAGE_VOL_CONTROL: {
      yield put(actions.setType('leverage'))
      yield put(actions.setLeverageType('vol-control'))
      yield put(actions.rehydrateLeverageVolControl(currentOrPending))
      break
    }

    case StrategyId.PROTECTION_CONTINUOUS: {
      yield put(actions.setType('protection'))
      yield put(actions.setProtectionType('continuous'))
      yield put(actions.rehydrateProtectionContinuous(currentOrPending))
      break
    }

    case StrategyId.PROTECTION_TERM: {
      yield put(actions.setType('protection'))
      yield put(actions.setProtectionType('term'))
      yield put(actions.rehydrateProtectionTerm(currentOrPending))
      break
    }
  }
  yield put(actions.validateStrategy())

  yield put(actions.setReadonlyCurrentOrPending(currentOrPending.currentAccountState))
  yield put(actions.setReadonlyCurrentOrPending(currentOrPending.pendingAccountState))

  yield reloadPerformanceAndProjectionSaga()
}

function* loadStateSaga(action: PayloadAction<SavedState>) {
  const { account } = action.payload
  const { currentAccountState, newAccount, pendingAccountState } = account
  const tradeStatus = action.payload.status

  const isNewAccount = newAccount === true
  const isActiveStatus = tradeStatus === TradeActivationStep.active
  // 2337: for new accounts, we always use pending state
  let shouldUsePendingState = isNewAccount

  // If NOT a new account, and there is some saved pending strategy state then use it
  if (!isNewAccount && pendingAccountState.portfolio) {
    shouldUsePendingState = true
  }

  previousParams = undefined
  yield put(actions.resetStrategy())

  const portfolioDisallowedStrategies = shouldUsePendingState
    ? getDisallowedStrategiesFromPendingState(account.pendingAccountState as any)
    : getDisallowedStrategiesFromCurrentState(currentAccountState as any, account.businessUnit)

  if (isNewAccount && !isActiveStatus && !pendingAccountState.strategy) {
    return
  }

  // yield put(actions.reset())
  // set client, set accountid
  yield put(actions.initExisting({ client: account.client, accountId: account.accountId }))
  yield put(
    actions.setStrategiesExisting({
      disallowedStrategies: portfolioDisallowedStrategies,
      isTransfer: pendingAccountState.deposit === undefined
    })
  )
  yield put(actions.setIsInited())

  yield fetchReferenceDataSaga()

  const rootState: RootState = yield select()
  const state = rootState.strategy

  const selectedStrategyTemplateId = shouldUsePendingState
    ? pendingAccountState.strategyTemplateId
    : currentAccountState.strategyTemplateId
  if (!selectedStrategyTemplateId) return

  const strategyTemplate = state.strategyTemplates.find(
    x => x.strategyTemplateId === selectedStrategyTemplateId
  )

  if (!strategyTemplate) {
    throw new Error('Strategy template not found')
  }

  const currentOrPending = {
    pendingAccountState,
    currentAccountState,
    shouldUsePendingState
  }
  yield rehydrateStrategySaga(strategyTemplate, currentOrPending)

  yield reloadPerformanceAndProjectionSaga()
}

function* fetchStrategiesAndCashInstruments() {
  const rootState: RootState = yield select()
  const noteMatDate = rootState.note.note.endDate
  const { fundingSource } = rootState.terms.terms
  try {
    const [strategyTemplates, cashInstruments] = yield all([
      call(api.tenant.strategyDetail, TENANT_ID),
      call(api.tenant.cashinstrumentDetail, TENANT_ID)
    ])

    yield put(
      actions.fetchReferenceDataSuccess({
        strategyTemplates: strategyTemplates.data,
        cashInstruments: cashInstruments.data,
        noteMatDate,
        isTransfer: fundingSource === FundingSource.Transfer
      })
    )
  } catch (e) {
    yield put(actions.fetchReferenceDataFailure(e))
  }
}

const fetchAssetsSaga = createFetchSaga(
  'strategyStep/fetchAssets',
  call(api.tenant.assetDetail, TENANT_ID)
)

function* fetchReferenceDataSaga() {
  yield all([fetchAssetsSaga(), fetchStrategiesAndCashInstruments()])
}

function getChartInvestmentAmount(state: RootState) {
  const parsedInvestmentAmount = Number(state.terms.terms.investment)
  if (Number.isFinite(parsedInvestmentAmount) && parsedInvestmentAmount > 0) {
    return parsedInvestmentAmount
  }

  if (state.terms.terms.fundingSource === FundingSource.Transfer) {
    const transferAmount = Number(state.portfolio.selfSelect.totalPortfolioValue)
    if (Number.isFinite(transferAmount) && transferAmount > 0) {
      return transferAmount
    }
  }

  return 1 // default to 1 if no investment value is available
}

function* fetchPerformanceSaga(params) {
  const state: RootState = yield select()
  const investmentAmount = getChartInvestmentAmount(state)

  yield put(actions.beginFetchBacktest())
  try {
    const result = yield call(api.tenant.tradeBacktestCreate, TENANT_ID, params)
    const data = result.data as ApiTradeBacktestResponse
    const timeseries = data.backtestTimeseries
    const multiplied: Record<string, number> = {}
    for (const date of Object.keys(timeseries)) {
      multiplied[date] = timeseries[date] * investmentAmount
    }

    yield put(actions.fetchBacktestSuccess(multiplied))
  } catch (e) {
    yield put(actions.fetchBacktestFailure())
  }
}

function* fetchProjectionSaga(params) {
  const state: RootState = yield select()
  const investmentAmount = getChartInvestmentAmount(state)

  yield put(actions.beginFetchProjection())
  try {
    const result = yield call(api.tenant.tradeProjectionCreate, TENANT_ID, params)

    const data = result.data as ApiProjectionResponse
    const timeserieses = Object.values(data)
    const multiplied: Record<string, number>[] = []

    for (const timeseries of timeserieses) {
      const values: Record<string, number> = {}
      for (const date of Object.keys(timeseries)) {
        values[date] = timeseries[date] * investmentAmount
      }

      multiplied.push(values)
    }

    yield put(actions.fetchProjectionSuccess(multiplied))
  } catch (e) {
    yield put(actions.fetchProjectionFailure())
  }
}

let previousParams

function* reloadPerformanceAndProjectionSaga() {
  const state: RootState = yield select()
  const { strategy } = state

  if (!strategy.selectedStrategy.type) {
    return
  }

  // validate before requesting charts
  const validationResult = validate(strategy)

  if (!validationResult.isValid) {
    yield put(actions.setDataIncomplete(validationResult.errors))
    return
  }

  yield put(actions.setDataComplete())

  const params: ApiTradePerformanceCalculation = {
    accountId: strategy.accountId,
    strategy: mapStrategy(strategy),
    protectionWeights: mapProtectionPortfolioWeights(strategy)
  }

  if (_.isEqual(params, previousParams)) {
    return
  }

  previousParams = { ...params }

  yield spawn(fetchPerformanceSaga, params)
  yield spawn(fetchProjectionSaga, params)
}

/**
 * This listens for actions, it filters them so they are only from the strategyStep
 * and also only actions that begin "set". This is so other non-relevant actions
 * are ignored.
 *
 * If an action it is interested in does happen, the performance and projection charts
 * are refreshed.
 *
 * It also validates all the data and determines whether the user can proceed to the next
 * step or not.
 *
 * @param action potentially any action from any slice
 * @returns
 */
function* reloadChartsInResponseToActionsSaga(action) {
  if (!action.type.startsWith('strategyStep/')) {
    return
  }

  if (!action.type.startsWith('strategyStep/set')) {
    return
  }

  // ignore certain actions
  if (
    action.type === 'strategyStep/setInvalid' ||
    action.type === 'strategyStep/setDataComplete' ||
    action.type === 'strategyStep/setDataIncomplete'
  ) {
    return
  }

  yield reloadPerformanceAndProjectionSaga()
}

export function* strategyStepRootSaga() {
  yield all([
    takeLatest('strategyStep/initNew', fetchReferenceDataSaga),
    debounce(
      500,
      (action: { type: string }) =>
        action.type.startsWith('strategyStep/set') &&
        action.type !== 'strategyStep/setInvalid' &&
        action.type !== 'strategyStep/setDataComplete' &&
        action.type !== 'strategyStep/setDataIncomplete',
      reloadChartsInResponseToActionsSaga
    ),
    takeLatest('strategyStep/loadState', loadStateSaga)
  ])
}

export default strategySlice.reducer
